<script lang="ts">
  import ProductLineGroup from "@retailer/svelte/ProductLines/Group.svelte";
  import BannerComponent from "@shared/svelte/Components/Banner/Banner.svelte";
  import ShoppableUnit from "@shared/svelte/Components/ShoppableUnit/ShoppableUnit.svelte";
  import { cloneDeep, slice, isEmpty } from "lodash";

  import type { ProductLine } from "@shared/types/ProductLine";
  import type { Banner } from "@shared/types/Banner";
  import type { ProductIndexBanner } from "@shared/types/ProductIndexBanner";
  import type { ShoppableUnit as ShoppableUnitType } from "@shared/types/ShoppableUnit";

  export let productLines: ProductLine[];
  export let gridBanners: ProductIndexBanner[] = [];
  export let shoppableUnit: ShoppableUnitType | any = {};
  export let stateParams: any;
  export let mobileMode: boolean;
  export let useStoreForCardData: boolean = true;
  export let trackProductIndexLayout: boolean = false;

  interface GridElement {
    type: string;
    testId: string;

    // If type === "product_grid"
    productLines?: ProductLine[];
    nextIndex?: number;

    // If type === "banner"
    banners?: Banner[];

    // If type === "shoppable_unit"
    shoppableUnit?: ShoppableUnitType;
  }

  let productsPerRow: number;
  let newProductsPerRow: number;
  let currentMobileMode: boolean = mobileMode;
  let gridWidth: number;
  let cardWidth: number;
  let gridElements: GridElement[] = [];

  $: {
    const adProductLineIds = productLines.filter(p => p.is_paid).map(p => p.id);
    productLines = productLines.filter(p => {
      if (p.is_paid) {
        return true;
      }

      return !adProductLineIds.includes(p.id);
    });

    const exactGridPerRow: number = gridWidth / cardWidth;
    if (exactGridPerRow < 3) {
      productsPerRow = 2;
    } else if (exactGridPerRow < 4) {
      productsPerRow = 3;
    } else {
      productsPerRow = 4;
    }
    if (shouldRebuildGrid()) {
      gridElements = rebuildGrid(productsPerRow);
      newProductsPerRow = productsPerRow;
      currentMobileMode = mobileMode;
    }
  }

  const shouldRebuildGrid = (): boolean => {
    if (mobileMode === false || mobileMode === true) {
      return newProductsPerRow !== productsPerRow || mobileMode !== currentMobileMode;
    }
    return false;
  };

  const bannerRowsForLayout = (): number[] => {
    if (gridBanners.length === 0) {
      return [];
    }
    if (isEmpty(shoppableUnit)) {
      return [2];
    } else {
      return mobileMode ? [6] : [5];
    }
  };

  const rebuildGrid = (perRow): GridElement[] => {
    let res: GridElement[] = [];
    let currentRow: number = 0;
    let nextProductCardIndex: number = 0;
    let nextBannerRowsIndex: number = 0;
    let nextShoppableRowIndex: number = 0;
    let productGridN: number = 0;
    let gridEl: number = 0;

    let remainingProductLines: ProductLine[] = cloneDeep(productLines);
    const bannerRows = bannerRowsForLayout();
    const shoppableUnitRows = isEmpty(shoppableUnit) ? [] : [2];
    while (remainingProductLines.length) {
      if (currentRow === shoppableUnitRows[nextShoppableRowIndex]) {
        res = res.concat([
          {
            type: "shoppable_unit",
            shoppableUnit,
            testId: `${gridEl}_shoppable_unit_${nextShoppableRowIndex}_row_${currentRow}`,
          },
        ]);
        currentRow += 1;
        nextShoppableRowIndex += 1;
      } else if (currentRow === bannerRows[nextBannerRowsIndex]) {
        res = res.concat([
          {
            type: "banner",
            banners: gridBanners[0]?.banners || [],
            testId: `${gridEl}_banner_${nextBannerRowsIndex}_row_${currentRow}`,
          },
        ]);
        currentRow += 1;
        nextBannerRowsIndex += 1;
      } else {
        let rowsOfProductLines: number;
        let numProductLinesInGrid: number;
        let productLinesInGrid: ProductLine[];
        if (shoppableUnitRows[nextShoppableRowIndex]) {
          rowsOfProductLines = shoppableUnitRows[nextShoppableRowIndex] - currentRow;
          numProductLinesInGrid = rowsOfProductLines * perRow;
          if (numProductLinesInGrid > remainingProductLines.length) {
            numProductLinesInGrid = remainingProductLines.length;
          }
          productLinesInGrid = slice(remainingProductLines, 0, numProductLinesInGrid);
        } else if (bannerRows[nextBannerRowsIndex]) {
          rowsOfProductLines = bannerRows[nextBannerRowsIndex] - currentRow;
          numProductLinesInGrid = rowsOfProductLines * perRow;
          if (numProductLinesInGrid > remainingProductLines.length) {
            numProductLinesInGrid = remainingProductLines.length;
          }
          productLinesInGrid = slice(remainingProductLines, 0, numProductLinesInGrid);
        } else {
          numProductLinesInGrid = remainingProductLines.length;
          rowsOfProductLines = numProductLinesInGrid / perRow;
          productLinesInGrid = remainingProductLines;
        }
        res = res.concat([
          {
            type: "product_grid",
            productLines: productLinesInGrid,
            nextIndex: nextProductCardIndex,
            testId: `${gridEl}_product_grid_${productGridN}_${nextProductCardIndex}-${
              nextProductCardIndex + numProductLinesInGrid - 1
            }`,
          },
        ]);
        productGridN += 1;
        nextProductCardIndex += numProductLinesInGrid;
        remainingProductLines = slice(remainingProductLines, numProductLinesInGrid);
        currentRow += rowsOfProductLines;
      }
      gridEl += 1;
    }
    return res;
  };
</script>

<style>
  .outer-container {
    max-width: 1139px;
    margin-inline: auto;
  }
  .display-margin {
    margin-top: 2rem;
  }
</style>

<div class="fizz-styles" bind:clientWidth={gridWidth} data-testid="product-line-grid">
  {#each gridElements as gridElement}
    {#if gridElement.type === "product_grid"}
      <div
        class="fizz-grid product-line-grid-element outer-container"
        style="--grid-col-min: 160px; --grid-col-max: 230px;"
        data-testid={gridElement.testId}
      >
        <ProductLineGroup
          {stateParams}
          productLines={gridElement.productLines}
          startingIndex={gridElement.nextIndex}
          bind:cardWidth
          {useStoreForCardData}
          {trackProductIndexLayout}
        />
      </div>
    {/if}
    {#if gridElement.type === "banner"}
      <div
        data-testid={gridElement.testId}
        class="product-line-grid-element banner-in-grid display-margin"
      >
        <BannerComponent banners={gridElement.banners} {mobileMode} />
      </div>
    {/if}
    {#if gridElement.type === "shoppable_unit"}
      <div
        data-testid={gridElement.testId}
        class="product-line-grid-element fizz-stack-32 display-margin"
        style="width: 100%"
      >
        <ShoppableUnit {shoppableUnit} />
      </div>
    {/if}
  {/each}
</div>
